import React, { useState } from 'react';
import { CardNumberElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Formik } from 'formik';
import { Col, Row } from 'react-bootstrap';
import * as yup from 'yup';

import { PrimaryButton } from '@magoosh/lib/button';
import { Form } from '@magoosh/lib/forms';
import useAuthenticityToken from '@magoosh/utils/use_authenticity_token';
import { bugsnagNotify } from 'utilities/bugsnag';
import { formatPrice } from 'utilities/money';

import * as api from '@payments/api';
import MultiConfirmationFormGroup from '@payments/components/form_groups/multi_confirmation';
import PaymentMethodsFormGroup from '@payments/components/form_groups/payment_methods';
import UserFormGroup from '@payments/components/form_groups/user';
import ScrollToError from '@payments/components/scroll_to_error';
import EuTerms from '@payments/components/terms/eu';
import UsTerms from '@payments/components/terms/us';
import { genericErrorMessage, PaymentError } from '@payments/errors';
import { BulkSelfServeFormProps, BulkSelfServeFormValues, PaymentMethod, PaymentStyle } from '@payments/types';

import style from '@payments/style.module.scss';

const validationSchema = yup.object({
  cardName: yup.string().label('Full Name'),
  email: yup.string().label('Email Address').when('signedIn', {
    is: false,
    then: yup.string().required()
  }),
  orderItems: yup.array(
    yup.object({
      purchasableCount: yup.number().label('Accounts').min(0)
    })
  ),
  password: yup.string().label('Password').when('signedIn', {
    is: false,
    then: yup.string().required()
  }),
  passwordConfirmation: yup.string().label('Confirm Password').when('signedIn', {
    is: false,
    then: yup.string().required()
  }),
  termsAndPolicyAccepted: yup.bool().oneOf([true], 'Terms of Use and Privacy Policy must be accepted'),
  zipCode: yup.string().required().label('Billing ZIP')
});

const OrderFormGroups: React.FC<BulkSelfServeFormProps> = (props) => {
  const authenticityToken = useAuthenticityToken();
  // Formik automatically sets isSubmitting to false when our async handleSubmit finishes which
  // leads to the submit button being enabled for a bit before the user is redirected. To ensure
  // that the submit button stays disabled we manage our own state.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const elements = useElements();
  const stripe = useStripe();

  const initialValues: BulkSelfServeFormValues = {
    cardName: '',
    email: '',
    orderItems: props.subscriptionPlans.map((plan) => {
      return {
        purchasable: plan,
        purchasableCount: plan.initialAmount ? plan.initialAmount : 0,
        priceCentsPerItem: plan.priceCents
      };
    }),
    password: '',
    passwordConfirmation: '',
    paymentMethod: PaymentMethod.STRIPE,
    paymentStyle: PaymentStyle.FULLPAYMENT,
    signedIn: props.signedIn,
    termsAndPolicyAccepted: props.signedIn || !props.isEuUser,
    zipCode: ''
  };

  const handleOnSubmit = async (values: BulkSelfServeFormValues, { setErrors }) => {
    setIsSubmitting(true);

    try {
      // Something with Stripe integration did not get set up correctly.
      if (values.paymentMethod == PaymentMethod.STRIPE && (!stripe || !elements)) {
        throw new PaymentError(
          genericErrorMessage,
          '`stripe` or `elements` unexpectedly did not exist'
        );
      }

      let valuesForSubmit = { ...values, authenticityToken };

      // Bulk orders (the only kind of purchase that currently use the Orders system)
      // don't yet support Stripe payments that create and save a Payment Method,
      // so instead we'll create a single-use token.
      const cardNumberElement = elements.getElement(CardNumberElement);
      const createStripeTokenResponse = await api.createStripeToken(stripe, cardNumberElement);
      valuesForSubmit = { ...valuesForSubmit, ...createStripeTokenResponse };

      const response = await api.createOrder(stripe, valuesForSubmit);
      if (response.nextUrl) {
        window.location.assign(response.nextUrl);
      } else {
        // Should not get here!
        throw new PaymentError(
          'We ran into an issue with your payment. Please try again and if you are still having trouble, contact us at <a href="mailto:help@magoosh.com">help@magoosh.com</a>.',
          `Unexpectedly reached the end of handleOnSubmit in SubscriptionFormGroups with values ${JSON.stringify(valuesForSubmit)}`
        );
      }
    } catch(exception) {
      setErrors(exception.errors);
      bugsnagNotify(exception.debug);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <h1>Purchase Your Magoosh Plans</h1>

      <Formik
        initialValues={initialValues}
        onSubmit={handleOnSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={validationSchema}>
        {({ values, handleSubmit, errors }) => {
          const price = values.orderItems.reduce((sum, orderItem) => (
            sum + (orderItem.purchasableCount * orderItem.priceCentsPerItem)
          ), 0);
          const requirePersonalizedPackage =
            values.orderItems.reduce((total, item) => {
              return total + item.purchasableCount;
            }, 0) >= 100;

          return (
            <Form id="subscription_payment_fields" className={style.formikForm}>
              <ScrollToError />
              {!props.signedIn && <UserFormGroup />}

              <PaymentMethodsFormGroup
                allowedPaymentMethods={[PaymentMethod.STRIPE]}
                paymentMethod={values.paymentMethod}
                paymentStyle={values.paymentStyle}
                totalExcludingTax={price}
              />

              <fieldset className="inputs">
                <div className="form-group">
                  <legend>
                    <span>Confirmation</span>
                  </legend>

                  <Row>
                    <Col xs={10} md={4}>
                      <MultiConfirmationFormGroup
                        errors={errors}
                        orderItems={values.orderItems}
                        requirePersonalizedPackage={requirePersonalizedPackage}
                      />
                    </Col>
                  </Row>
                </div>
              </fieldset>

              {props.isEuUser && !props.signedIn && <EuTerms buttonName="Complete Order" />}

              <div className="form-group">
                {requirePersonalizedPackage ? (
                  <PrimaryButton
                    className={style.btnBlockOnMobile}
                    onClick={() => {
                      window.open('mailto:schools@magoosh.com', '_blank');
                    }}>
                    Contact Us
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    className={style.btnBlockOnMobile}
                    onClick={handleSubmit}
                    submitting={isSubmitting}
                    type="submit"
                  >
                    Complete Order
                  </PrimaryButton>
                )}
              </div>

              {!props.isEuUser && <UsTerms buttonName="Complete Order" />}
            </Form>
          );
        }}
      </Formik>
    </>
  );
  return <div />;
};

export const BulkRegistrationForm: React.FC<BulkSelfServeFormProps> = (props) => {
  const StripeElementsWrapper = ({ children }) => {
    const stripePromise = loadStripe(props.stripeToken);
    return <Elements stripe={stripePromise}>{children}</Elements>;
  };

  return (
    <StripeElementsWrapper>
      <OrderFormGroups {...props} />
    </StripeElementsWrapper>
  );
};

export default BulkRegistrationForm;
